import React, { useCallback, useState } from "react";
import { NavLink } from "react-router-dom";
import Swal from "sweetalert2";
import { orderList, orderGet, orderCancel } from "../../../api/bseApi";
import {
  buildOrderListPayload,
  buildOrderGetPayload,
  buildOrderCancelPayload,
  parseOrderListResponse,
  parseOrderGetResponse,
  extractBseApiError,
  isOrderCancellable,
} from "../../../utils/bseOrder";

const DEFAULT_MEMBER_ID = process.env.REACT_APP_BSE_MEMBER_ID || "63164";
const DEFAULT_UCC = "DematUCCTest9";

const cardStyle = {
  background: "#ffffff",
  borderRadius: "8px",
  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
  padding: "20px",
  marginBottom: "20px",
};

const tealBorder = "#06d1c6";
const inputStyle = {
  width: "100%",
  padding: "8px 12px",
  border: `1px solid ${tealBorder}`,
  borderRadius: "6px",
  backgroundColor: "#fff",
  color: "#334155",
  fontSize: "14px",
};

const labelStyle = {
  fontWeight: 600,
  color: "#334155",
  fontSize: "14px",
  marginBottom: "6px",
};

function formatOrderDate(value) {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatAmount(amount) {
  const n = Number(amount);
  if (!Number.isFinite(n)) return "-";
  return `₹${n.toLocaleString("en-IN")}`;
}

function statusBadgeClass(status) {
  const s = String(status || "").toLowerCase();
  if (["done", "matched", "success"].includes(s)) return "bg-success";
  if (["received", "pending", "open"].includes(s)) return "bg-warning text-dark";
  if (["failed", "rejected", "cancelled"].includes(s)) return "bg-danger";
  return "bg-secondary";
}

export default function MyOrdersAdmin() {
  const [orderType, setOrderType] = useState("system");
  const [search, setSearch] = useState(DEFAULT_UCC);
  const [transactionType, setTransactionType] = useState("purchase");
  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    d.setMonth(d.getMonth() - 1);
    return d.toISOString().slice(0, 10);
  });
  const [endDate, setEndDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [viewType, setViewType] = useState("on-screen");
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [orderError, setOrderError] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState("");
  const [cancelLoading, setCancelLoading] = useState(false);

  const openCloseFilter = orderType === "system" ? "o" : "a";

  const fetchOrders = useCallback(async () => {
    const clientCode = search.trim();
    if (!clientCode) {
      setOrderError("Enter client UCC (Search) to fetch orders.");
      setOrders([]);
      return;
    }

    setLoading(true);
    setOrderError(null);
    try {
      const types =
        transactionType === "sell" ? ["r"] : transactionType === "purchase" ? ["p"] : ["p", "r"];
      const payload = buildOrderListPayload({
        memberId: DEFAULT_MEMBER_ID,
        clientCode,
        openClose: openCloseFilter,
        types,
        start: 0,
        length: 100,
      });

      const res = await orderList(payload);
      const body = res?.data || {};
      if (body.status !== "success") {
        throw new Error(extractBseApiError({ response: { data: body } }));
      }

      let rows = parseOrderListResponse(body);
      const from = startDate ? new Date(`${startDate}T00:00:00`) : null;
      const to = endDate ? new Date(`${endDate}T23:59:59`) : null;
      if (from || to) {
        rows = rows.filter((row) => {
          if (!row.date) return true;
          const placed = new Date(row.date);
          if (Number.isNaN(placed.getTime())) return true;
          if (from && placed < from) return false;
          if (to && placed > to) return false;
          return true;
        });
      }

      setOrders(rows);
    } catch (err) {
      setOrders([]);
      setOrderError(extractBseApiError(err));
    } finally {
      setLoading(false);
    }
  }, [search, openCloseFilter, transactionType, startDate, endDate]);

  const handleApply = (e) => {
    e.preventDefault();
    fetchOrders();
  };

  const handleViewOrder = async (row) => {
    setSelectedOrder({ orderNo: row.orderNo, loading: true });
    setDetailLoading(true);
    setDetailError("");
    try {
      const payload = buildOrderGetPayload(row.orderNo, openCloseFilter);
      const res = await orderGet(payload);
      const body = res?.data || {};
      if (body.status !== "success") {
        throw new Error(extractBseApiError({ response: { data: body } }));
      }
      const detail = parseOrderGetResponse(body);
      if (!detail) throw new Error("Order details not found");
      setSelectedOrder(detail);
    } catch (err) {
      setDetailError(extractBseApiError(err));
      setSelectedOrder({ orderNo: row.orderNo });
    } finally {
      setDetailLoading(false);
    }
  };

  const handleCancelOrder = async (row) => {
    const orderId = row.orderNo;
    const confirm = await Swal.fire({
      icon: "warning",
      title: "Cancel order?",
      text: `Cancel BSE order #${orderId}? This cannot be undone.`,
      showCancelButton: true,
      confirmButtonColor: "#dc2626",
      confirmButtonText: "Cancel order",
    });
    if (!confirm.isConfirmed) return;

    setCancelLoading(true);
    try {
      const res = await orderCancel(buildOrderCancelPayload(orderId));
      const body = res?.data || {};
      if (body.status !== "success") {
        throw new Error(extractBseApiError({ response: { data: body } }));
      }
      await Swal.fire({ icon: "success", title: "Order cancelled", timer: 2000, showConfirmButton: false });
      setSelectedOrder(null);
      fetchOrders();
    } catch (err) {
      Swal.fire({ icon: "error", title: "Cancel failed", text: extractBseApiError(err) });
    } finally {
      setCancelLoading(false);
    }
  };

  const closeDetailModal = () => {
    setSelectedOrder(null);
    setDetailError("");
  };

  return (
    <div className="main-content">
      <div className="page-wrapper page-settings">
        <div className="content">
          <div className="mb-4" style={cardStyle}>
            <div className="content-page-header content-page-headersplit">
              <h5>My Orders</h5>
            </div>
            <nav aria-label="breadcrumb" style={{ "--bs-breadcrumb-divider": "none", marginBottom: "15px" }}>
              <ol className="breadcrumb">
                <li className="breadcrumb-item">
                  <NavLink to="/dashboard">Dashboard</NavLink> /
                </li>
                <li className="breadcrumb-item">
                  <NavLink to="/admin/invest-bse/profile-list">Invest Online BSE</NavLink> /
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  My Orders
                </li>
              </ol>
            </nav>
            <p className="small text-muted mb-0">
              BSE v2 APIs: <code>/api/bse/order_list</code>, <code>/order_get</code>, <code>/order_cancel</code>
            </p>
          </div>

          <div
            style={{
              background: "#f1f5f9",
              borderRadius: "12px",
              padding: "24px",
              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.06)",
            }}
          >
            <form onSubmit={handleApply}>
              <div className="mb-4">
                <div style={{ ...labelStyle, marginBottom: "8px" }}>Order Type :</div>
                <div className="d-flex gap-4">
                  <label className="d-flex align-items-center gap-2 mb-0" style={{ cursor: "pointer" }}>
                    <input
                      type="radio"
                      name="orderType"
                      checked={orderType === "system"}
                      onChange={() => setOrderType("system")}
                      style={{ accentColor: "#10b981" }}
                    />
                    Open Orders
                  </label>
                  <label className="d-flex align-items-center gap-2 mb-0" style={{ cursor: "pointer" }}>
                    <input
                      type="radio"
                      name="orderType"
                      checked={orderType === "all"}
                      onChange={() => setOrderType("all")}
                      style={{ accentColor: "#10b981" }}
                    />
                    All Orders
                  </label>
                </div>
              </div>

              <div className="mb-4">
                <div style={{ ...labelStyle, marginBottom: "12px" }}>Filters :</div>
                <div className="row g-3">
                  <div className="col-12 col-md-6 col-lg-4">
                    <label className="form-label small text-secondary mb-1 d-block">Search (Client UCC):</label>
                    <input
                      type="text"
                      placeholder="Client UCC"
                      value={search}
                      onChange={(e) => {
                        setSearch(e.target.value);
                        setOrderError(null);
                      }}
                      style={inputStyle}
                    />
                  </div>
                  <div className="col-12 col-md-6 col-lg-4">
                    <div style={{ ...labelStyle, marginBottom: "8px" }}>Transaction Type :</div>
                    <div className="d-flex gap-3">
                      <label className="d-flex align-items-center gap-2 mb-0" style={{ cursor: "pointer" }}>
                        <input
                          type="radio"
                          name="transactionType"
                          checked={transactionType === "purchase"}
                          onChange={() => setTransactionType("purchase")}
                          style={{ accentColor: "#10b981" }}
                        />
                        Purchase
                      </label>
                      <label className="d-flex align-items-center gap-2 mb-0" style={{ cursor: "pointer" }}>
                        <input
                          type="radio"
                          name="transactionType"
                          checked={transactionType === "sell"}
                          onChange={() => setTransactionType("sell")}
                          style={{ accentColor: "#10b981" }}
                        />
                        Sell
                      </label>
                    </div>
                  </div>
                  <div className="col-12 col-md-6 col-lg-4">
                    <label className="form-label small text-secondary mb-1 d-block">From date</label>
                    <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} style={inputStyle} />
                  </div>
                  <div className="col-12 col-md-6 col-lg-4">
                    <label className="form-label small text-secondary mb-1 d-block">To date</label>
                    <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} style={inputStyle} />
                  </div>
                </div>
              </div>

              <div className="mb-4">
                <div style={{ ...labelStyle, marginBottom: "8px" }}>View Type :</div>
                <div className="d-flex gap-4">
                  <label className="d-flex align-items-center gap-2 mb-0" style={{ cursor: "pointer" }}>
                    <input
                      type="radio"
                      name="viewType"
                      checked={viewType === "on-screen"}
                      onChange={() => setViewType("on-screen")}
                      style={{ accentColor: "#10b981" }}
                    />
                    On Screen
                  </label>
                  <label className="d-flex align-items-center gap-2 mb-0" style={{ cursor: "pointer" }}>
                    <input
                      type="radio"
                      name="viewType"
                      checked={viewType === "excel"}
                      onChange={() => setViewType("excel")}
                      style={{ accentColor: "#10b981" }}
                    />
                    Excel
                  </label>
                </div>
              </div>

              <div className="text-center">
                <button
                  type="submit"
                  disabled={loading}
                  style={{
                    background: tealBorder,
                    color: "#fff",
                    border: "none",
                    padding: "10px 40px",
                    borderRadius: "8px",
                    fontWeight: 600,
                    fontSize: "15px",
                    cursor: loading ? "wait" : "pointer",
                    opacity: loading ? 0.8 : 1,
                  }}
                >
                  {loading ? "Loading…" : "Apply"}
                </button>
              </div>
            </form>
          </div>

          {orderError && (
            <div className="mb-3 small text-danger" style={cardStyle}>
              {orderError}
            </div>
          )}

          {viewType === "on-screen" && (
            <div style={{ ...cardStyle, overflowX: "auto" }}>
              <h6 className="mb-3" style={{ fontWeight: 600, color: "#334155" }}>Order Status</h6>
              <table className="table table-bordered mb-0" style={{ fontSize: "13px" }}>
                <thead style={{ backgroundColor: "#f8fafc" }}>
                  <tr>
                    <th style={{ padding: "10px 12px" }}>Order No</th>
                    <th style={{ padding: "10px 12px" }}>Date</th>
                    <th style={{ padding: "10px 12px" }}>Scheme</th>
                    <th style={{ padding: "10px 12px" }}>Type</th>
                    <th style={{ padding: "10px 12px" }}>Amount</th>
                    <th style={{ padding: "10px 12px" }}>Status</th>
                    <th style={{ padding: "10px 12px" }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={7} className="text-center py-4 text-muted">
                        Loading orders from BSE…
                      </td>
                    </tr>
                  ) : orders.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="text-center py-4 text-muted">
                        No orders found. Enter a UCC and click Apply.
                      </td>
                    </tr>
                  ) : (
                    orders.map((row) => (
                      <tr key={`${row.orderNo}-${row.memOrdRefId}`}>
                        <td style={{ padding: "10px 12px" }}>{row.orderNo}</td>
                        <td style={{ padding: "10px 12px" }}>{formatOrderDate(row.date)}</td>
                        <td style={{ padding: "10px 12px" }}>
                          <div>{row.scheme}</div>
                          {row.schemeCode && <div className="small text-muted">{row.schemeCode}</div>}
                        </td>
                        <td style={{ padding: "10px 12px" }}>{row.transactionType}</td>
                        <td style={{ padding: "10px 12px" }}>{formatAmount(row.amount)}</td>
                        <td style={{ padding: "10px 12px" }}>
                          <span className={`badge ${statusBadgeClass(row.status)}`}>{row.status}</span>
                        </td>
                        <td style={{ padding: "10px 12px" }}>
                          <div className="d-flex gap-2 flex-wrap">
                            <button
                              type="button"
                              className="btn btn-sm"
                              style={{ background: tealBorder, color: "#fff", border: "none" }}
                              onClick={() => handleViewOrder(row)}
                            >
                              View
                            </button>
                            {isOrderCancellable(row.status) && (
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-danger"
                                disabled={cancelLoading}
                                onClick={() => handleCancelOrder(row)}
                              >
                                Cancel
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {selectedOrder && (
        <div
          className="modal fade show d-block"
          style={{ background: "rgba(15, 23, 42, 0.55)" }}
          onClick={closeDetailModal}
        >
          <div className="modal-dialog modal-dialog-centered modal-lg" onClick={(e) => e.stopPropagation()}>
            <div className="modal-content border-0 shadow-lg" style={{ borderRadius: "12px" }}>
              <div className="modal-header border-0" style={{ background: tealBorder, color: "#fff" }}>
                <h5 className="modal-title">Order #{selectedOrder.orderNo}</h5>
                <button type="button" className="btn-close btn-close-white" onClick={closeDetailModal} aria-label="Close" />
              </div>
              <div className="modal-body">
                {detailLoading ? (
                  <p className="text-muted mb-0">Loading order details…</p>
                ) : detailError ? (
                  <div className="alert alert-danger mb-0">{detailError}</div>
                ) : (
                  <div className="row g-3">
                    <div className="col-md-6"><strong>Status:</strong> {selectedOrder.status}</div>
                    <div className="col-md-6"><strong>Amount:</strong> {formatAmount(selectedOrder.amount)}</div>
                    <div className="col-md-6"><strong>Scheme:</strong> {selectedOrder.scheme}</div>
                    <div className="col-md-6"><strong>UCC:</strong> {selectedOrder.ucc}</div>
                    <div className="col-md-6"><strong>Placed:</strong> {formatOrderDate(selectedOrder.placedAt)}</div>
                    <div className="col-md-6"><strong>Mode:</strong> {selectedOrder.physOrDemat}</div>
                    {selectedOrder.remarks && (
                      <div className="col-12"><strong>Remarks:</strong> {selectedOrder.remarks}</div>
                    )}
                  </div>
                )}
              </div>
              <div className="modal-footer border-0">
                {!detailLoading && !detailError && isOrderCancellable(selectedOrder.status) && (
                  <button
                    type="button"
                    className="btn btn-outline-danger"
                    disabled={cancelLoading}
                    onClick={() => handleCancelOrder(selectedOrder)}
                  >
                    Cancel order
                  </button>
                )}
                <button type="button" className="btn btn-secondary" onClick={closeDetailModal}>
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
